/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {setTrackingAssetSnapToRoad} from '../store/actions/tracking';
import {isTrackingAssetSnapToRoadEnabledSelector} from '../store/selectors/tracking-selectors';
import {getAssetHistorySetting} from './useAssetHistorySettings';

/**
 * The useAssetSnapToRoad hook restores the Asset History Snap-to-Road
 * setting from the user's persisted preference once on mount, dispatching
 * an update only when the stored value differs from the current Redux
 * state, and returns the current enabled/disabled value for the component
 * to render and toggle.
 *
 * ```typescript
 *
 *  import {useAssetSnapToRoad} from '../../hooks/useAssetSnapToRoad';
 *
 *  const isAssetSnapToRoadEnabled = useAssetSnapToRoad();
 *
 * ```
 */
export const useAssetSnapToRoad = (): boolean => {
  const dispatch                 = useDispatch();
  const isAssetSnapToRoadEnabled = useSelector(isTrackingAssetSnapToRoadEnabledSelector);

  useEffect(() => {
    const persistedSnapToRoad = getAssetHistorySetting()?.isAssetSnapToRoadEnabled;
    if (persistedSnapToRoad !== undefined && persistedSnapToRoad !== isAssetSnapToRoadEnabled) {
      dispatch(setTrackingAssetSnapToRoad(persistedSnapToRoad));
    }
  }, []);

  return isAssetSnapToRoadEnabled;
};

export default useAssetSnapToRoad;
